import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ITagModel, ITagType } from 'dist/lib-core-shared/lib/models/product/dto/tags/product-tags.model';
import { ProductTagsService, RolePermissionService, UtilitiesService } from 'lib-core-shared';
import { debounceTime, filter, tap } from 'rxjs/operators';

@Component({
  selector: 'app-product-tag-search',
  templateUrl: './product-tag-search.component.html',
  styleUrls: ['./product-tag-search.component.scss']
})
export class ProductTagSearchComponent implements OnInit {
  form: FormGroup;
  searchInput!: string;
  searchResults?: ITagModel[];
  disableCreateButton: boolean = true;
  @Input() existingProductTags?: ITagModel[];
  selectedTags:ITagModel[] = [];
  @Output() tagsToBeAdded = new EventEmitter<any>();
  @ViewChild('createNewTagModal') public createNewTagModal!: ElementRef;
  @ViewChild('editTagModal') public editTagModal!: ElementRef;
  tagToEditId?: number;
  newlyAddedProductId?: number;
  @Input() searchResultsShownInTagsAdmin?: boolean;
  fb: any;
  tagTypes!: ITagType[];
  canCreate: boolean = false;
  charactersReqForAutoSearch: number = 2;

  constructor(
    private productTagsService: ProductTagsService, 
    private changeDetectorRef: ChangeDetectorRef,
    public modalService: NgbModal,
    private utilitiesService: UtilitiesService,
    private roleService: RolePermissionService
  ) {
    this.form = new FormGroup({
      query: new FormControl('')
    })

    this.canCreate = this.roleService.userHasPermission('ProductManagement.Tag.Create');
  }

  ngOnInit() {
    this.watchInput();
    this.changeDetectorRef.detectChanges();
    this.getTagTypes();
  }

  watchInput() {
    this.form.controls.query.valueChanges.pipe(
      tap(query => {
        delete this.searchResults;
        this.searchInput = query;
        this.disableCreateButton = true;
        this.selectedTags = [];
        if (!query) {
          this.newlyAddedProductId = undefined;
        }
        delete this.fb;
      }),
      debounceTime(750),
      filter(query => query.length >= this.charactersReqForAutoSearch),
    ).subscribe((query) => {
      this.productTagsService.search(query).subscribe(res => {
        if (res) {
          this.searchResults = this.removeExistingTagsAndOrder(res.tags);
          if (this.newlyAddedProductId) {
            this.addNewTagFlag();
          }
          this.disableCreateButton = res.tags.length ? true : false;
        } 
      });
    })
  }

  removeExistingTagsAndOrder(tags: ITagModel[]) {
    if (this.existingProductTags && this.existingProductTags.length) {
      // filter() removes any existing tags from the response. sort() orders the results alphabetically by name value
      return tags.filter(tag => !this.existingProductTags?.some(exProdTag => tag.id === exProdTag.id)).sort((a, b) => a.name.localeCompare(b.name));
    } else {
      return tags.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  tagSelected(event: any) {
    if (event.isTagSelected === true) {
      this.selectedTags.push(event.tag);
    } else if (event.isTagSelected === false) {
      let index = this.selectedTags.indexOf(event.tag);

      if (index != -1) {
        this.selectedTags.splice(index, 1)
      }
    } else {
      this.tagToEditId = event.tag.id;

      if (this.tagToEditId) {
        this.editTag();      
      }
    }
  }

  addSelectedTags() {
    if (this.selectedTags && this.selectedTags.length) {
      this.tagsToBeAdded.emit(this.selectedTags);
      this.form.controls.query.setValue('');
      this.selectedTags = [];
    }
  }

  createNewTag() {
    delete this.fb;
    this.modalService.open(this.createNewTagModal, {backdrop: 'static', keyboard: false, size: 'md'});
  }

  editTag() {
    delete this.fb;
    this.modalService.open(this.editTagModal, {backdrop: 'static', keyboard: false, size: 'md'});
  }

  closeModal() {
    if (this.tagToEditId) {
      this.tagToEditId = undefined;
    }

    this.modalService.dismissAll();
  }

  saveCloseModal(event: any) {
    this.closeModal();
    this.newlyAddedProductId = undefined;

    if (event) {
      if (event.mode === "create" && event.response && event.response.tagId) {
        this.newlyAddedProductId = event.response.tagId;
      }

      if (event.mode === "create" && event.response && event.response.name) {
        // set search input value as newly created tag name
        this.form.controls.query.setValue(event.response.name);
      }

      if (event.mode === "edit" && this.searchInput) {
        // set search input value as previously used query
        this.form.controls.query.setValue(this.searchInput);
      }
    }

    this.selectedTags = [];
  }

  closeDropdownPanel() {
    // clears the search input and watchInput() gets called due to the form control valueChange, this then clears fb and selectedTags variable
    this.form.controls.query.setValue('');
  }

  addNewTagFlag() {
    if (this.searchResults && this.searchResults.length) {
      this.searchResults.forEach(r => {
        if (r.id === this.newlyAddedProductId) {
          r.newTag = true;
        }
      })
    }
  }

  deleteTag(tag: any) {
    delete this.fb;
    const formValue: string = this.form.controls.query.value;

    const processDeletion = () => {
      this.productTagsService.deleteTag(tag.id).subscribe(res => {
        if (res) {
          this.form.controls.query.setValue(formValue);
        }
      }, error => {
        if (error && error.status !== 404) {   
          this.fb = {
            text: error.error,
            error: error
          };
                
          setTimeout(() => {
            let errorCard = document.getElementsByTagName('lib-error');
            errorCard[0].scrollIntoView({block: "end", inline: "center", behavior: "smooth"});
          }, 0)
        }
      });
    };
    
    let modalMessage = tag.name ? `Are you sure you want to delete the tag "${tag.name}"?` : `Are you sure you want to delete this tag?`;

    this.utilitiesService.generalConfirmation(processDeletion, modalMessage);
  }

  getTagTypes() {
    this.productTagsService.getTagTypes().subscribe(res => {
      this.tagTypes = res;
    });
  }

  public getErrorMsgText():string {
    return this.fb?.text ? this.fb.text : '';
  }
}